import type { FC } from "react";
import { Link } from "react-router";
import {
  ReturnWizard,
  type ReturnWizardSubmitPayload,
  type ReturnWizardSubmitResult,
} from "../../../../components/return-form/ReturnWizard";
import { normalizeOrderCode } from "../../../../libs/mypage/orderCode";
import { Container } from "../../components/Container";
import type { OrderDetailDTO } from "../../components/format";
import { PageHeading } from "../../components/PageHeading";
import { useDecodedOrderCode } from "../../hooks/useDecodedOrderCode";
import { useOrder } from "../../hooks/useOrder";
import { apiClient } from "../../rpc";
import { ActiveReturnView } from "./ActiveReturnView";
import { BackLink } from "./BackLink";
import { uploadReturnAttachmentViaMyPage } from "./mypageUploadAttachment";

export const ReturnFormPage: FC = () => {
  const code = useDecodedOrderCode();
  const result = useOrder(code);

  if (result.status === "loading") {
    return (
      <Container>
        <p className="py-8 text-center text-neutral-500 text-sm">読み込み中…</p>
      </Container>
    );
  }
  if (result.status === "not-found") {
    return (
      <Container>
        <PageHeading title="ご注文が見つかりませんでした" />
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }
  if (result.status === "error") {
    return (
      <Container>
        <PageHeading title="注文情報を取得できませんでした" description={result.message} />
        <Link to="/dashboard" className="text-sm underline underline-offset-4">
          注文履歴に戻る
        </Link>
      </Container>
    );
  }

  return <ReturnFormBody order={result.order} />;
};

const meReturn = apiClient.me.orders[":code"].return;

/**
 * mypage 経路の submit 実装。共有 ReturnWizard に注入する。`apiClient` (session cookie / CSRF
 * 付与) で `POST /mypage/api/me/orders/:code/return` を叩き、レスポンスを ReturnWizard が扱う
 * 共通結果型に正規化する。
 */
const submitViaMyPage =
  (slug: string) =>
  async (payload: ReturnWizardSubmitPayload): Promise<ReturnWizardSubmitResult> => {
    const res = await meReturn.$post({ param: { code: slug }, json: payload });
    if (res.status === 200) {
      const data = await res.json();
      return { ok: true, returnRequestCode: data.returnRequestCode };
    }
    const data = (await res.json().catch(() => ({}))) as { message?: unknown };
    return {
      ok: false,
      message:
        typeof data.message === "string" ? data.message : `申請に失敗しました (HTTP ${res.status})`,
    };
  };

const ReturnFormBody: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  // 既存の進行中 ReturnRequest がある場合は新規申請を出さず案内のみ
  if (order.activeReturnRequest) {
    return <ActiveReturnView order={order} />;
  }
  const slug = normalizeOrderCode(order.code);
  // 両 track ineligible の場合も「対象外」画面で弾かず、ウィザードの診断ステップに流して
  // 各 track の判定理由をユーザに直接見せる (= 「お届け前」「期間外」「対象 SKU 無し」等)。
  return (
    <ReturnWizard
      order={order}
      defectiveOk={order.returnEligibility.defective.eligible}
      customerReasonOk={order.returnEligibility.customerReason.eligible}
      campaignOk={order.returnEligibility.campaign.eligible}
      onSubmit={submitViaMyPage(slug)}
      uploadAttachment={uploadReturnAttachmentViaMyPage}
      backLink={<BackLink slug={slug} />}
    />
  );
};
